import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import type { Project } from "@shared/types";
import { projectBasePath } from "@domain/project/paths";
import { db } from "../firebase";

/**
 * Base for repositories that wrap a single Firestore doc at
 *   <projectBasePath>/data/{key}
 *
 * The base path depends on the project's `schema` marker:
 *   - legacy → users/{ownerUid}/projects/{id}/data/{key}
 *   - v2    → projects/{id}/data/{key}
 *
 * The Firestore doc shape is `{ value: T }` — that's a historical convention
 * from useFirestoreDoc; keeps arrays/primitives/objects storable uniformly.
 *
 * Subclasses only need to declare the key + default value + static type.
 */
export abstract class ProjectDocRepository<T> {
  /** Firestore doc key under <projectBasePath>/data/ */
  abstract readonly key: string;

  /** What to return when the doc doesn't exist. */
  abstract readonly defaultValue: T;

  private ref(project: Project) {
    return doc(db, projectBasePath(project), "data", this.key);
  }

  /** Subscribe to real-time updates. Returns unsubscribe fn. */
  watch(
    project: Project,
    onChange: (value: T, loaded: boolean) => void
  ): Unsubscribe {
    return onSnapshot(
      this.ref(project),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          onChange(
            data.value !== undefined ? (data.value as T) : this.defaultValue,
            true
          );
        } else {
          onChange(this.defaultValue, true);
        }
      },
      (err) => {
        console.error(`[${this.constructor.name}] watch error`, err);
        onChange(this.defaultValue, true);
      }
    );
  }

  /** One-shot read. */
  async get(project: Project): Promise<T> {
    const snap = await getDoc(this.ref(project));
    if (!snap.exists()) return this.defaultValue;
    const data = snap.data();
    return data.value !== undefined ? (data.value as T) : this.defaultValue;
  }

  /**
   * Replace the doc with the given value. Caller is responsible for passing
   * the full desired state — this is not a partial-update helper.
   */
  async set(project: Project, value: T): Promise<void> {
    await setDoc(this.ref(project), { value }, { merge: true });
  }

  /**
   * Seed the default value if the doc doesn't exist yet. Idempotent.
   * Only callable by the owner — shared members shouldn't seed on behalf
   * of the owner.
   */
  async seedIfMissing(project: Project): Promise<void> {
    const existing = await getDoc(this.ref(project));
    if (existing.exists()) return;
    await this.set(project, this.defaultValue);
  }
}
